<template lang="pug">
app-main-content-layout(title='')
  .c-users-page
    //- Header Section
    section.c-users-page__header
      .c-users-page__header-content
        h1.c-users-page__heading Users
        p.c-users-page__subtitle Manage user accounts and permissions

    //- Table Section
    section.c-users-table-section
      CmsListToolbar(
        :tabs="viewTabs"
        :active-tab="currentView"
        v-model="searchQuery"
        search-placeholder="Search users by name or email..."
        :sort-value="sortBy"
        :sort-options="sortOptions"
        :selected-count="selected.length"
        @tab-change="handleTabChange"
        @search="searchUsers"
        @update:sortValue="sortBy = $event"
        @sort="sortTable"
        @clear-selection="tableRef?.clearSelection()"
      )
        template(#bulk)
          el-select.c-users-bulk-select(
            v-model="setAs"
            placeholder="Set as"
            @change="bulkUpdate(setAs.value)"
          )
            el-option(
              v-for="item in setAsOption"
              :key="item.value"
              :label="item.label"
              :value="item"
            )

      //- Skeleton Loader
      template(v-if="loading")
        .c-skeleton-loader
          .c-skeleton-row(v-for="n in 10" :key="n")
            .c-skeleton-cell.c-skeleton-cell--checkbox
            .c-skeleton-cell.c-skeleton-cell--name
            .c-skeleton-cell.c-skeleton-cell--email
            .c-skeleton-cell.c-skeleton-cell--status

      //- Table
      el-table(
        v-else
        ref="tableRef"
        :data="items.items"
        @row-click="routeToUser($event)"
        style="width: 100%"
        @selection-change="onCheckboxChange"
        ).clickable-rows
        el-table-column(type="selection" width="55")
        el-table-column(label="Name" min-width="180")
          template(#default="scope")
            .crm-person
              .crm-avatar {{ getInitials(scope.row) }}
              span.crm-name {{ getDisplayName(scope.row) }}
        el-table-column(label="Email" min-width="300")
          template(#default="scope")
            .crm-meta
              crm-icon.crm-meta__icon(name="mail")
              span.crm-meta__text {{ scope.row.email }}
        el-table-column(label="Phone" min-width="140" v-if="currentView != 'invited'")
          template(#default="scope")
            .crm-meta(v-if="scope.row.phone")
              crm-icon.crm-meta__icon(name="phone")
              | {{ scope.row.phone }}
            span.crm-empty(v-else) —
        el-table-column(label="Organization" min-width="180" v-if="currentView != 'invited'")
          template(#default="scope")
            .crm-meta.crm-meta--strong(v-if="getOrgNames(scope.row)")
              crm-icon.crm-meta__icon(name="building")
              | {{ getOrgNames(scope.row) }}
            span.crm-empty(v-else) —
        el-table-column(label="Type" min-width="110" v-if="currentView != 'invited'")
          template(#default="scope")
            span.crm-badge(:class="getTypeBadge(scope.row.usertype).class")
              | {{ getTypeBadge(scope.row.usertype).label }}
        el-table-column(prop="active" label="Status" min-width="110")
          template(#default="scope")
            span.crm-badge.crm-badge--teal(v-if="scope.row.active")
              crm-icon(name="check-circle")
              | Active
            span.crm-badge.crm-badge--gray(v-else-if="scope.row.active == false")
              crm-icon(name="clock")
              | Archived
            span.crm-badge.crm-badge--yellow(v-else-if="scope.row.entity === 'User'")
              crm-icon(name="alert-circle")
              | Invited

      //- Pagination
      .c-users-pagination(v-if="currentView != 'invited'")
        el-pagination(
          layout="prev, pager, next"
          :current-page="items.paginationMetadata.currentPage"
          :total="items.paginationMetadata.totalPageCount * pageSize"
          @current-change="setPage"
        )

    //- Results count
    .c-users-results(v-if="!loading && items.items.length > 0")
      span(v-if="currentView != 'invited'")
        | Showing {{ items.items.length }} of {{ items.paginationMetadata.totalItemCount }} users
      span(v-else) Showing {{ items.items.length }} invited users

  dialog-add-user(
    v-if="dialog"
    v-model="dialog"
    @save-item="organizationInvite"
    :server-error="serverError"
    @close-dialog="dialog=false"
  )
</template>

<script>
import {
  defineComponent, ref, onMounted, reactive,
} from 'vue';
import CmsListToolbar from '@/components/CmsListToolbar.vue';
import shared from './shared';
import DialogAddUser from './DialogAddUser.vue';
import CrmIcon from './CrmIcon.vue';

export default defineComponent({
  name: 'ManageContent_Users',
  components: {
    DialogAddUser, CmsListToolbar, CrmIcon,
  },
  setup() {
    const {
      // route,
      router,
      loading,
      getUsers,
      createOrganizationInvite,
      doGetInvites,
      doUpdateUserIsActiveMutation,
    } = shared();
    const items = reactive({ items: [], paginationMetadata: {} });
    const invited = reactive({ items: [] });
    const serverError = ref('');
    const dialog = ref(false);
    const filter = ref('');
    const currentView = ref('ALL');
    const selected = ref([]);
    const searchQuery = ref('');
    const tableRef = ref();
    const viewTabs = [
      { label: 'All', value: 'ALL' },
      { label: 'Active', value: 'active' },
      { label: 'Archived', value: 'archived' },
      { label: 'Invited', value: 'invited' },
    ];
    const isFiltered = ref(false);
    const sortOptions = [{ label: 'First Name', value: 'name' }, { label: 'Last Name', value: 'last_name' }, { label: 'Email', value: 'email' }, { label: 'Recent', value: 'created_at_desc' }, { label: 'Oldest', value: 'created_at' }];
    const setAsOption = [{ label: 'Archived', value: 'archive' }];
    const setAs = ref('');
    const sortBy = ref('');
    const page = ref(1);
    const pageSize = 10;

    const setPage = (newPage) => {
      items.paginationMetadata.currentPage = newPage;
      findUsers();
    };

    function routeToUser(itm) {
      if (currentView.value !== 'invited') {
        router.push(`/manage/users/${itm.id}/edit`);
      } else {
        router.push(`/manage/invites/${itm.id}/info`);
      }
    }

    async function findUsers() {
      try {
        loading.value = true;
        const usersWithPaginationMetadata = await getUsers(searchQuery.value, sortBy.value.value, filter.value, items.paginationMetadata.currentPage, pageSize);
        items.items = usersWithPaginationMetadata.users;
        items.paginationMetadata = usersWithPaginationMetadata.paginationMetadata;
      } catch (err) {
        console.log(err);
      } finally {
        loading.value = false;
      }
    }

    async function organizationInvite(item) {
      try {
        loading.value = true;
        await createOrganizationInvite(item);
        dialog.value = false;
      } catch (err) {
        serverError.value = err.message;
      } finally {
        loading.value = false;
      }
    }

    function getDisplayName(row) {
      return currentView.value === 'invited' ? row.name : `${row.firstname || ''} ${row.lastname || ''}`.trim();
    }

    function getOrgNames(row) {
      return (row.organizations || []).map((org) => org.name).join(', ');
    }

    function getInitials(row) {
      const name = getDisplayName(row) || row.email || '';
      return name
        .split(/\s+/)
        .filter(Boolean)
        .slice(0, 2)
        .map((part) => part[0].toUpperCase())
        .join('');
    }

    function getTypeBadge(usertype) {
      switch (usertype) {
        case 'ADMIN':
          return { label: 'Admin', class: 'crm-badge--blue' };
        case 'ORG':
          return { label: 'Org Admin', class: 'crm-badge--teal' };
        default:
          return { label: 'User', class: 'crm-badge--purple' };
      }
    }

    const bullhornsTruncateSize = 4;

    function getBullhorns(bullhorns) {
      console.log(bullhorns);
      return [];
    }

    function showOnboardedStatus(item) {
      if (item.usertype === 'ORG') {
        if (item.organization) {
          return 'Onboarded';
        }
      }
      return '--';
    }

    function searchUsers() {
      if (searchQuery.value.length > 0) {
        isFiltered.value = true;
        findUsers();
      } else {
        isFiltered.value = false;
        findUsers();
      }
    }

    function sortTable() {
      if (sortBy.value.value) {
        findUsers();
      }
    }

    function onCheckboxChange(checkedItems) {
      selected.value = checkedItems;
    }

    function getClass(currentItem) {
      if (currentItem === currentView.value) {
        return 'filter-active';
      }
      return 'filter-inactive';
    }

    function getButtonClass(currentItem) {
      switch (currentItem) {
        case 'Pending':
          return 'pending-button';
        case 'Not Approved':
          return 'notapproved-button';
        default:
          return 'approved-button';
      }
    }

    function clearSearch() {
      searchQuery.value = '';
      isFiltered.value = false;
      headerFilter(currentView.value);
      findUsers();
    }

    function headerFilter(filterBy) {
      currentView.value = filterBy;
      searchQuery.value = '';
      filter.value = filterBy;
      items.paginationMetadata.currentPage = 1;
      if (filterBy === 'invited') {
        items.items = invited.items.filter((item) => item.entity === 'User');
      } else {
        findUsers();
      }
    }

    function handleTabChange(tabName) {
      headerFilter(tabName);
    }

    async function bulkUpdate(type) {
      // Check if any users are selected
      if (selected.value.length === 0) {
        // Handle case when no users are selected
        return;
      }

      try {
        // Create an array of user IDs from the selected users
        const userIds = selected.value.map((user) => user.id);

        // Determine the new `Active` status based on the `type` parameter
        const activeStatus = type !== 'archive';

        // Prepare the input objects for the GraphQL mutation
        const inputList = userIds.map((userId) => ({
          id: userId,
          active: activeStatus,
        }));

        // Define the input variable with the expected structure
        const input = inputList;

        // Make the GraphQL request using your client library
        await doUpdateUserIsActiveMutation(input);

        // Clear the selected users
        selected.value = [];

        // Fetch updated user data
        await findUsers();
      } catch (err) {
        serverError.value = err.message;
      }
    }

    async function findInvites() {
      try {
        loading.value = true;
        invited.items = await doGetInvites();
      } catch (err) {
        console.log(err);
      } finally {
        loading.value = false;
      }
    }

    onMounted(async () => {
      await findUsers();
      await findInvites();
    });

    return {
      // data (static)
      loading,
      tableRef,
      viewTabs,
      bullhornsTruncateSize,

      // data (reactive)
      items,
      invited,
      serverError,
      input: ref(),
      dialog,
      filter,
      selected,
      searchQuery,
      isFiltered,
      currentView,
      sortOptions,
      setAsOption,
      setAs,
      sortBy,
      page,
      pageSize,
      // methods
      getDisplayName,
      getOrgNames,
      getInitials,
      getTypeBadge,
      getBullhorns,
      showOnboardedStatus,
      organizationInvite,
      routeToUser,
      getClass,
      getButtonClass,
      headerFilter,
      onCheckboxChange,
      bulkUpdate,
      searchUsers,
      sortTable,
      clearSearch,
      findInvites,
      setPage,
      handleTabChange,
    };
  },
});
</script>

<style lang="scss" scoped>
@import './crm-list';

.c-users-page {
  @include crm-page;
}

@include crm-header('c-users-page');
@include crm-table('c-users-table-section');
@include crm-cells;
@include crm-pagination('c-users-pagination');
@include crm-results('c-users-results');
@include crm-skeleton;

.crm-meta--strong {
  color: $foreground;
  font-weight: 500;
}

.c-users-bulk-select {
  width: 200px;

  :deep(.el-select__wrapper) {
    @include crm-control-skin;
    font-size: 0.875rem;
  }
}

:deep(.el-checkbox__label) {
  display: none;
}

@media (max-width: 768px) {
  .c-users-page {
    padding: 1.5rem;
  }
}

@media (max-width: 480px) {
  .c-users-page {
    padding: 1rem;
  }
}
</style>
